import { memo } from 'react';
import type { PlayerPublic } from '@ringmaster/shared';
import { SpeakingRing, MutedMic } from '../../discord/voice/VoiceIndicators.js';
import { LevelDiamond } from '../../ui/LevelDiamond.js';
import { LEVEL_OVERHANG, LEVEL_SCALE } from '../../discord/desktop/Medallion.js';

/**
 * PlayerBust — one player's portrait medallion + name plate for the Stage lineup.
 *
 * Portrait is the avatar image when present, else an ornate initial-medallion
 * fallback (the player's initial in Grenze on an ink disc inside a gold ring).
 * The four `state`s are the reveal-choreography vocabulary: 'normal' at rest,
 * 'dim' when another bust is spotlit, 'spotlit' when this is the focus, 'dead'
 * when the player has been killed (grayscale + skull marker).
 *
 * Authored against the 1920×1080 BroadcastFrame design space, so `size` is px.
 *
 * `frameTier` is a cosmetic seam for future portrait-frame cosmetics
 * (plain / gilded / ember). All tiers render plain today — the prop just keeps
 * the call-site contract stable so cosmetics can drop in with no signature change.
 */

/** The minimal player shape a bust needs (a structural subset of PlayerPublic). */
export type BustPlayer = Pick<PlayerPublic, 'id' | 'name' | 'alive'> & {
  avatarUrl?: string | null;
  /** Discord user id — drives the talking-glow ring + muted-mic (Activity only). */
  discordUserId?: string | null;
  /**
   * Lifetime level — the same `LevelDiamond` the desktop medallions wear, in the
   * same proportion, pinned bottom-CENTRE (the skull marker owns bottom-right).
   * Null/absent draws nothing.
   */
  level?: number | null;
};

export type BustState = 'normal' | 'dim' | 'spotlit' | 'dead';
export type FrameTier = 'plain' | 'gilded' | 'ember';

export interface PlayerBustProps {
  player: BustPlayer;
  /** Portrait diameter in design-space px. Default 120. */
  size?: number;
  state?: BustState;
  /** Cosmetic frame tier (all render plain today). */
  frameTier?: FrameTier;
}

function initialOf(name: string): string {
  return (name.trim()[0] ?? '?').toUpperCase();
}

function PlayerBustImpl({ player, size = 120, state = 'normal', frameTier = 'plain' }: PlayerBustProps) {
  const dead = state === 'dead';
  const spotlit = state === 'spotlit';
  const dim = state === 'dim';

  // frameTier is a seam only — no per-tier styling yet.
  void frameTier;

  const ring = spotlit
    ? '3px solid var(--gold-5, #e6b866)'
    : dead
      ? '2px solid var(--state-dead, #9c8f86)'
      : '2px solid var(--gold-3, #a9762f)';

  const glow = spotlit
    ? 'var(--rim-gold, 0 0 24px rgba(230,184,102,.5))'
    : 'none';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: Math.round(size * 0.08),
        opacity: dim ? 0.42 : 1,
        transition: 'opacity var(--duration-base, 280ms) var(--ease-standard)',
        filter: dead ? 'grayscale(1) brightness(0.55)' : 'none',
      }}
    >
      {/* Portrait disc + its level badge. The badge lives on this wrapper, not
          inside the disc: the disc clips (overflow: hidden) and the diamond has
          to hang over its bottom edge. */}
      <div
        style={{
          position: 'relative',
          marginBottom: player.level != null ? Math.round(size * LEVEL_OVERHANG) : 0,
        }}
      >
        <div
          style={{
            position: 'relative',
            width: size,
            height: size,
            borderRadius: '50%',
            border: ring,
            boxShadow: glow,
            background: 'var(--ink-2, #150d0f)',
            overflow: 'hidden',
            display: 'grid',
            placeItems: 'center',
            transform: spotlit ? 'scale(1.06)' : 'scale(1)',
            transition:
              'transform var(--duration-base, 280ms) var(--ease-standard), box-shadow var(--duration-base, 280ms) var(--ease-standard), border-color var(--duration-base, 280ms) var(--ease-standard)',
          }}
        >
          <SpeakingRing discordUserId={player.discordUserId} />
          {player.avatarUrl ? (
            <img
              src={player.avatarUrl}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <span
              style={{
                fontFamily: 'var(--font-display, serif)',
                fontSize: Math.round(size * 0.5),
                lineHeight: 1,
                color: 'var(--gold-5, #e6b866)',
                userSelect: 'none',
              }}
            >
              {initialOf(player.name)}
            </span>
          )}

          {/* Dead marker slot — small skull badge on the disc. */}
          {dead && (
            <span
              aria-hidden
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: Math.round(size * 0.32),
                height: Math.round(size * 0.32),
                display: 'grid',
                placeItems: 'center',
                fontSize: Math.round(size * 0.2),
                background: 'var(--ink-1, #0b0709)',
                borderRadius: '50%',
                border: '1px solid var(--state-dead, #9c8f86)',
              }}
            >
              💀
            </span>
          )}
        </div>

        <span
          style={{
            position: 'absolute',
            bottom: -Math.round(size * LEVEL_OVERHANG),
            left: '50%',
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        >
          <LevelDiamond level={player.level} size={Math.round(size * LEVEL_SCALE)} />
        </span>
      </div>

      {/* Name plate */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          maxWidth: size * 1.6,
          padding: '4px 12px',
          background: 'var(--ink-2, #150d0f)',
          borderTop: '1px solid var(--gold-4, #c9923f)',
          borderRadius: 'var(--radius-sm, 8px)',
          fontFamily: 'var(--font-ui, Inter, sans-serif)',
          fontSize: Math.max(14, Math.round(size * 0.16)),
          fontWeight: 500,
          color: dead ? 'var(--state-dead, #9c8f86)' : 'var(--text-primary, #fff)',
          textAlign: 'center',
          textDecoration: dead ? 'line-through' : 'none',
        }}
      >
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {player.name}
        </span>
        <MutedMic discordUserId={player.discordUserId} size={Math.max(12, Math.round(size * 0.13))} />
      </div>
    </div>
  );
}

export const PlayerBust = memo(PlayerBustImpl);
